import React, { useState } from "react";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { Button, message } from "antd";
import NotificationTopicDrawer from "./NotificationTopicDrawer";
import NotificationTopicsTable from "./NotificationTopicsTable";
import useNotificationTopics from "hooks/notification-topics/useNotificationTopics";
import useRoles from "hooks/access-control/useRoles";
import useUsers from "hooks/userManagement/useUsers";
import TopicAssignmentsDrawer from "./TopicAssignmentsDrawer";
import { toast } from "react-toastify";
import { PlusOutlined } from "@ant-design/icons";
import useMessage from "antd/es/message/useMessage";
import useMessageTemplates from "hooks/message-templates/useMessageTemplates";

export default function NotificationTopicsPage() {
  const {
    notificationTopics,
    loading,
    fetchAllTopics,
    createTopic,
    updateTopic,
    deleteTopic,
    bulkAssignRolesToTopic,
    bulkAssignUsersToTopic,
    removeRoleFromTopic,
    removeUserFromTopic,
    addChannelToTopic,
    removeChannelFromTopic,
    assignTemplateToTopic,
    removeTemplateFromTopic,
    totalElements,
    page,
    pageSize,
    setPage,
    setPageSize,
  } = useNotificationTopics();
  const { roles } = useRoles();
  const { users } = useUsers();
  const { messageTemplates } = useMessageTemplates();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [assignmentVisible, setAssignmentVisible] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleTableChange = (pagination) => {
    if (pagination.pageSize !== pageSize) {
      setPage(0);
    } else {
      setPage(pagination.current - 1);
    }

    setPageSize(pagination.pageSize);
  };

  const openDrawer = (topic = null) => {
    setEditingTopic(topic);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setEditingTopic(null);
    setDrawerVisible(false);
  };

  const openAssignments = (topic) => {
    setSelectedTopic(topic);
    setAssignmentVisible(true);
  };

  const closeAssignments = () => {
    setAssignmentVisible(false);
    setSelectedTopic(null);
  };

  // submit handler
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingTopic) {
        const current = values.channels;
        const initial = values.initialChannels;

        const currentTypes = current.map((c) => c.type);
        const initialTypes = initial.map((c) => c.type);

        const added = current.filter((c) => !initialTypes.includes(c.type));
        const removed = initial.filter((c) => !currentTypes.includes(c.type));

        await updateTopic(values, editingTopic.id);

        // add
        if (added) {
          await Promise.all(
            added.map((ch) => addChannelToTopic(editingTopic.id, ch.id)),
          );
        }

        // remove
        if (removed) {
          await Promise.all(
            removed.map((ch) => removeChannelFromTopic(editingTopic.id, ch.id)),
          );
        }
      } else {
        await createTopic(values);
      }
      closeDrawer();
    } catch (err) {
      message.error("Error saving topic");
    } finally {
      setSubmitting(false);
    }
  };

  // delete topic
  const handleDelete = async (topicId) => {
    try {
      await deleteTopic(topicId);
      message.success("Topic deleted!");
    } catch {
      message.error("Failed to delete topic");
    }
  };

  // toggle enable/disable
  const handleToggleEnable = async (topicId, enabled) => {
    try {
      await updateTopic({ isEnabled: enabled }, topicId);
    } catch {
      message.error("Failed to update topic");
    }
  };

  const handleUpdateAssignments = async (topic, values, assignMode) => {
    if (!topic?.id) return;

    setSubmitting(true);

    try {
      // handle roles
      if (assignMode === "roles") {
        const rolesToAdd = values.roles || [];
        const payload = {
          roleIds: rolesToAdd,
          description: values.roleDescription,
        };

        await bulkAssignRolesToTopic(topic.id, payload);
        toast.success("Role(s) assigned to topic");
      }

      // handle users
      if (assignMode === "users") {
        const usersToAdd = values.users || [];
        const payload = {
          userIds: usersToAdd,
          description: values.userDescription,
        };

        await bulkAssignUsersToTopic(topic.id, payload);
        toast.success("User(s) assigned to topic");
      }

      if (assignMode === "templates") {
        const templateId = values.template;
        if (!templateId) return;

        // optimistic: only one template
        await assignTemplateToTopic(topic.id, templateId);
        toast.success("Template assigned to topic");
      }

      fetchAllTopics();
    } catch (err) {
      console.log(err);
      toast.error(
        assignMode === "roles"
          ? "Failed to assign roles"
          : assignMode === "users"
          ? "Failed to assign users"
          : "Failed to assign template",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveRole = async (topicId, roleId) => {
    setSubmitting(true);
    try {
      await removeRoleFromTopic(topicId, roleId);
      fetchAllTopics();
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveUser = async (topicId, userId) => {
    setSubmitting(true);
    try {
      await removeUserFromTopic(topicId, userId);
      fetchAllTopics();
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveTemplate = async (topicId, templateId) => {
    setSubmitting(true);
    try {
      await removeTemplateFromTopic(topicId, templateId);
    } catch (err) {
      console.log(err)
      toast.error("Failed to remove template");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon="bell"
          title="Notification Topics"
          subtitle="Manage all configurable system alerts"
          transform="shrink-2"
          className="flex-1"
        />

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openDrawer()}
          style={{ marginBottom: 16 }}
        >
          Create Topic
        </Button>
      </div>

      <NotificationTopicsTable
        topics={notificationTopics}
        loading={loading}
        onEdit={openDrawer}
        onToggleEnable={handleToggleEnable}
        onDelete={handleDelete}
        onManageAssignments={openAssignments}
        pagination={{
          current: page + 1,
          pageSize,
          total: totalElements,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
      />

      <NotificationTopicDrawer
        visible={drawerVisible}
        onClose={closeDrawer}
        onSubmit={handleSubmit}
        topic={editingTopic}
        roles={roles}
        users={users}
        loading={submitting}
      />

      <TopicAssignmentsDrawer
        visible={assignmentVisible}
        topic={selectedTopic}
        roles={roles}
        users={users}
        templates={messageTemplates}
        onSubmit={handleUpdateAssignments}
        onClose={closeAssignments}
        onRemoveRole={handleRemoveRole}
        onRemoveUser={handleRemoveUser}
        onRemoveTemplate={handleRemoveTemplate}
        loading={submitting}
      />
    </>
  );
}
